import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import api from "../../api/api";

/**
 * Props:
 * - type         : "teacher" | "course"  — determines API endpoint and title
 * - name         : string  — teacher or course name displayed
 * - entityId     : number  — teacher_id or course_id
 * - studentId    : number
 * - token        : string
 * - ratingId     : number | null  — existing rating id for PUT
 * - hasRated     : boolean | null — whether student has already rated
 * - initialRating: number | null  — pre-fill stars if already rated
 * - onRatingIdUpdate : fn  — callback to update ratingId in parent after POST
 * - onClose      : fn
 */
const RatingPopup = ({
  type = "teacher",
  name,
  entityId,
  studentId,
  token,
  ratingId,
  hasRated,
  initialRating,
  onRatingIdUpdate,
  onClose,
}) => {
  const [selectedRating, setSelectedRating] = useState(initialRating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isTeacher = type === "teacher";

  const title = isTeacher ? "Rate Teacher" : "Rate Course";
  const label = isTeacher ? `Teacher "${name}"` : `Course "${name}"`;

  const handleSubmit = async () => {
    if (!selectedRating || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const isNew = !hasRated || !ratingId;

      const payload = isTeacher
        ? { teacher: entityId, student: studentId, rating: selectedRating }
        : { course: entityId, student: studentId, rating: selectedRating };

      if (isNew) {
        const endpoint = isTeacher
          ? `/rating/teacher-rating-by-student/`
          : `rating/course-rating-by-student/`;

        const res = await api.post(endpoint, payload, {
          headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        });

        if (!isTeacher && res?.data?.id && onRatingIdUpdate) {
          onRatingIdUpdate(res.data.id);
        }
      } else {
        const endpoint = isTeacher
          ? `/rating/teacher-rating-by-student/${ratingId}/`
          : `rating/course-rating-by-student/${ratingId}/`;

        await api.put(endpoint, payload, {
          headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        });
      }

      setTooltipOpen(true);
      setTimeout(() => {
        setTooltipOpen(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Rating submission failed:", error);
      setSelectedRating(0);
      setTooltipOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div
      onClick={handleSkip}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "40px 32px",
          width: "720px",
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          position: "relative",
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleSkip}
          style={{
            position: "absolute",
            top: "14px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "22px",
            cursor: "pointer",
            color: "#888",
            lineHeight: 1,
          }}
        >
          &times;
        </button>

        {/* Title */}
        <h3 style={{ fontWeight: "700", fontSize: "20px", marginBottom: "8px", color: "#000" }}>
          {title}
        </h3>

        {/* Subtitle */}
        <p style={{ color: "#888", fontSize: "14px", marginBottom: "24px" }}>
          We'd love to know how your experience has been till now.
        </p>

        {/* Entity Name */}
        <p style={{ fontWeight: "600", fontSize: "16px", marginBottom: "16px", color: "#000" }}>
          {label}
        </p>

        {/* Stars */}
        <Tooltip
          title="Thank you for your feedback!"
          open={tooltipOpen}
          placement="top"
          arrow
          PopperProps={{ disablePortal: true }}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#fff",
                color: "#000",
                fontWeight: 600,
                fontSize: "14px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                borderRadius: "8px",
                padding: "8px 16px",
              },
            },
            arrow: { sx: { color: "#fff" } },
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "24px" }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <i
                key={star}
                className="fa-solid fa-star"
                onMouseEnter={() => !tooltipOpen && !isSubmitting && setHoverRating(star)}
                onMouseLeave={() => !tooltipOpen && !isSubmitting && setHoverRating(0)}
                onClick={() => !isSubmitting && setSelectedRating(star)}
                style={{
                  fontSize: "36px",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  color: star <= (hoverRating || selectedRating) ? "#f5a623" : "#e0e0e0",
                  transition: "color 0.15s ease",
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              />
            ))}
          </div>
        </Tooltip>

        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <button
            onClick={handleSkip}
            disabled={isSubmitting}
            style={{
              padding: "10px 28px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: "#fff",
              color: "#333",
              fontSize: "15px",
              fontWeight: 500,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            Skip for now
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedRating}
            style={{
              padding: "10px 28px",
              borderRadius: "8px",
              border: "none",
              background: selectedRating ? "#1a73e8" : "#a0c4f8",
              color: "#fff",
              fontSize: "15px",
              fontWeight: 500,
              cursor: isSubmitting || !selectedRating ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatingPopup;